import React, { useState, useEffect } from 'react';
import { Select, Button, message, Tooltip } from 'antd';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';



const { Option } = Select;

// This maps the spreadsheet column names to the formData keys
const fieldMapping = {
  "Comprehensive Plan Review": "comprehensive_plan_review",
  "Review Investment Policy Statement": "review_investment_policy_statement",
  "Review Investment Menu, Performance, and Providers": "review_investment_menu",
  "Select Alternatives To Address Over and Under Concentration": "select_alternatives",
  "Serve as a Co-Fiduciary Under ERISA": "serve_as_cofiduciary",
  "Assess fidelity bonding": "assess_fidelity_bonding",
  "Compliance Check & Fee Disclosure": "compliance_check",
  "Assess Current Employee Education Program": "assess_education_program",
  "Plan and Implement Effective Employee Education Program": "plan_education_program",
  "Targeted Education Meetings, Including Re-Enrollment": "targeted_education_meetings",
  "Limited One-On-One Support Via Phone": "one_on_one_support"
};

const AutomaticDataEntryForAdvisorServiceSchedule = ({ onAddRow, onDataSubmitted }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    comprehensive_plan_review: 'Annually',
    review_investment_policy_statement: 'Annually',
    review_investment_menu: 'Quarterly',
    select_alternatives: 'As Needed',
    serve_as_cofiduciary: 'Annually',
    assess_fidelity_bonding: 'Annually',
    compliance_check: 'Annually',
    assess_education_program: 'Annually',
    plan_education_program: 'Annually',
    targeted_education_meetings: 'Annually',
    one_on_one_support: 'Annually'
  });

  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyData, setCompanyData] = useState([]);

  // Fetch client names
  useEffect(() => {
    const fetchClientNames = async () => {
      try {
        const userId = localStorage.getItem('userid');
       const year = dayjs().year();; // or dynamically determined
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/client_names/${year}`, {
          headers: {
            'user-id': userId
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setCompanyOptions(data.client_names);
      } catch (error) {
        console.error('Error fetching client names:', error);
        message.error('Failed to load client list');
      }
    };

    fetchClientNames();
  }, []);

  // Fetch company data
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const userId = localStorage.getItem('userid');
        const year = new Date().getFullYear(); // or fixed as 2024
        
        // console.log("Fetching company data for Advisor Service Schedule...");
        
        const response = await fetch(
          `https://plansync-backend-234288438538.us-central1.run.app/api/spreadsheet/${userId}/Advisor Service Schedule/${year}`,
          {
            headers: {
              'user-id': userId
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // console.log("Received company data:", data);
        setCompanyData(data.data || []);
      } catch (error) {
        console.error('Error fetching company data:', error);
        message.error('Failed to load company data');
      }
    };

    // Call fetchCompanyData immediately when component mounts
    fetchCompanyData();
  }, []); // Empty dependency array to run once on mount

  // When company_name changes or companyData updates, prefill formData from the spreadsheet data
  useEffect(() => {
    if (formData.company_name && companyData.length > 0) {
      const selectedCompany = companyData.find(c => c["Company Name"] === formData.company_name);
      if (selectedCompany) {
        setFormData(prevFormData => {
          const updatedData = { ...prevFormData };
          for (const [spreadsheetKey, formKey] of Object.entries(fieldMapping)) {
            if (selectedCompany.hasOwnProperty(spreadsheetKey) && selectedCompany[spreadsheetKey] !== null) {
              updatedData[formKey] = selectedCompany[spreadsheetKey];
            }
          }
          return updatedData;
        });
      }
    }
  }, [formData.company_name, companyData]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const userId = localStorage.getItem('userid');
     const year = dayjs().year();; // or dynamically determined

      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/advisor-service/${year}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      message.success(data.message || 'Advisor service schedule updated successfully');

      // Clear form after successful submission or keep the data as needed
      setFormData({
        company_name: '',
        comprehensive_plan_review: 'Annually',
        review_investment_policy_statement: 'Annually',
        review_investment_menu: 'Quarterly',
        select_alternatives: 'Semi-Annually',
        serve_as_cofiduciary: 'Annually',
        assess_fidelity_bonding: 'Annually',
        compliance_check: 'Annually',
        assess_education_program: 'Semi-Annually',
        plan_education_program: 'Quarterly',
        targeted_education_meetings: 'Quarterly',
        one_on_one_support: 'As Needed'
      });

      if (onAddRow) onAddRow(formData);
      if (onDataSubmitted) onDataSubmitted();
    } catch (error) {
      console.error('Error submitting advisor service schedule:', error);
      message.error('Failed to submit advisor service schedule');
    }
  };

  const frequencyOptions = ['Yearly', 'Semi-Annually', 'Quarterly', 'Monthly', 'Weekly', 'Daily', 'As Needed', 'Never', 'Other'];

  const isFormValid = () => formData.company_name !== '';

  const renderSelect = (field, options, placeholder = '') => (
    <Select
      value={formData[field]}
      onChange={(value) => handleChange(field, value)}
      style={{ width: '100%' }}
      placeholder={placeholder}
      showSearch
      filterOption={(input, option) =>
        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
      optionFilterProp="children"
    >
      {options.map(option => (
        <Option key={option} value={option}>{option}</Option>
      ))}
    </Select>
  );

  const renderLabel = (label, tooltip) => (
    <div className="flex items-center">
      <span>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
      </Tooltip>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-2 bg-white rounded-2xl">
      <h1 className="text-3xl font-light text-gray-800 mb-6">Advisor Service Schedule Data Entry</h1>
      <form className="space-y-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {renderLabel("Company Name", "Select the company for which you're setting up the service schedule.")}
          </label>
          <Select
            value={formData.company_name}
            onChange={(value) => handleChange('company_name', value)}
            style={{ width: '100%' }}
            placeholder="Select company"
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            optionFilterProp="children"
          >
            {companyOptions.map(company => (
              <Option key={company} value={company}>{company}</Option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Comprehensive Plan Review", "How often will you conduct a complete review of the plan?")}
            </label>
            {renderSelect('comprehensive_plan_review', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Review Investment Policy Statement", "Frequency of reviewing and updating the investment policy statement.")}
            </label>
            {renderSelect('review_investment_policy_statement', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Review Investment Menu", "How often will you review and update the investment options?")}
            </label>
            {renderSelect('review_investment_menu', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Select Alternatives", "Frequency of reviewing and selecting alternative investment options.")}
            </label>
            {renderSelect('select_alternatives', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Serve as Co-Fiduciary", "Will you serve as a co-fiduciary for this plan?")}
            </label>
            {renderSelect('serve_as_cofiduciary', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Assess Fidelity Bonding", "How often will you review the fidelity bond coverage?")}
            </label>
            {renderSelect('assess_fidelity_bonding', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Compliance Check", "Frequency of reviewing plan compliance with regulations.")}
            </label>
            {renderSelect('compliance_check', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Assess Education Program", "How often will you evaluate the effectiveness of education programs?")}
            </label>
            {renderSelect('assess_education_program', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Plan Education Program", "Frequency of planning and updating education initiatives.")}
            </label>
            {renderSelect('plan_education_program', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("Targeted Education Meetings", "How often will you conduct focused education sessions?")}
            </label>
            {renderSelect('targeted_education_meetings', frequencyOptions)}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {renderLabel("One-on-One Support", "Availability for individual participant consultations.")}
            </label>
            {renderSelect('one_on_one_support', frequencyOptions)}
          </div>
        </div>

        <div className="flex justify-start">
          <Tooltip title="Save this service schedule to the database">
            <Button 
              onClick={handleSubmit}
              disabled={!isFormValid()}
              size="large"
              className={`inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-lg shadow-sm text-white ${isFormValid() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out`}
            >
              <CalendarIcon className="h-5 w-5 mr-2" />
              Submit Service Schedule
            </Button>
          </Tooltip>
        </div>
      </form>
    </div>
  );
};

export default AutomaticDataEntryForAdvisorServiceSchedule;
